/**
 * The Graph calls a publish issues, rendered for developer mode — the mock's `graphCallLines`
 * and friends (sap-mockup/components/AssignmentBlade.tsx:137), ported.
 *
 * THE PORT IS NOT LITERAL, ON PURPOSE. The mock is captioned "Indicative — the mock does not call
 * Graph"; ours does, so a preview that quietly differed from the real request would be a
 * confident lie. Every call below is written from the code that issues it:
 *
 *   groups      services/intune_publisher.py  `ensure_groups` — LOOK UP by displayName first,
 *                                              create only on a miss. The mock POSTs blind.
 *   app         services/intune_publisher.py  `app_data`
 *   content     services/intune_publisher.py  contentVersions → files → commit → PATCH
 *   assignment  services/intune_publisher.py  `assign_all` — ONE POST …/assign carrying the whole
 *                                              set, per spec §7a.2.
 *
 * The assignment body is built by handing each row to `rowToAssignmentCreate` — the same mapper
 * the real payload uses, pinned to the backend by a parity test — and then applying the backend's
 * own Graph translation. Nothing here is hand-written to look plausible.
 */
import { rowToAssignmentCreate } from './assignmentPayload'
import type { AssignmentRow, AssignmentGroup, GeneratedGroupName } from '../components/AssignmentBlade'

const json = (v: unknown) => JSON.stringify(v, null, 2)

/**
 * Port of `intune_publisher._group_mail_nickname` (:2664).
 *
 * mailNickname is REQUIRED on POST /groups even for a security group that will never receive
 * mail, and Graph rejects spaces and most punctuation, so the generated display name cannot be
 * used verbatim.
 */
export function groupMailNickname(displayName: string): string {
  let cleaned = (displayName || '').trim().replace(/[^A-Za-z0-9_-]+/g, '-')
  cleaned = cleaned.replace(/-{2,}/g, '-').replace(/^[-_]+|[-_]+$/g, '')
  if (!cleaned) cleaned = 'aeam-group'
  return cleaned.slice(0, 64)
}

/** Resolve the generated groups: matched by name so a second app reuses one, created empty. */
export function graphGroupLines(names: string[]): string[] {
  return names.flatMap((name) => [
    `GET https://graph.microsoft.com/v1.0/groups?$filter=displayName eq '${name.replace(/'/g, "''")}'`,
    '',
    '# A hit here is REUSED — a second app on the same pattern must not create a duplicate.',
    '# On a miss:',
    'POST https://graph.microsoft.com/v1.0/groups',
    'Content-Type: application/json',
    '',
    json({
      displayName: name,
      mailEnabled: false,
      mailNickname: groupMailNickname(name),
      securityEnabled: true,
      groupTypes: [],
      description:
        'Created by Splashtop Agentic Packr from a deployment template naming pattern. ' +
        'Members are not added automatically.',
    }),
    '',
    '# 201 Created, and EMPTY. Scope: Group.Create',
    '# The returned id replaces the <created: …> placeholder in the assignment calls.',
    '',
  ])
}

export interface AppCreateFacts {
  displayName: string
  description?: string
  publisher?: string
  installContext?: 'system' | 'user'
  restartBehaviour?: string
  returnCodes?: { returnCode: number; type: string }[]
}

/** The win32LobApp create body, as `intune_publisher.py:1448` assembles it. */
export function graphCreateAppLines(f: AppCreateFacts): string[] {
  return [
    'POST https://graph.microsoft.com/v1.0/deviceAppManagement/mobileApps',
    'Content-Type: application/json',
    '',
    json({
      '@odata.type': '#microsoft.graph.win32LobApp',
      displayName: f.displayName,
      description: f.description ?? '',
      publisher: f.publisher ?? '',
      installExperience: {
        runAsAccount: f.installContext ?? 'system',
        deviceRestartBehavior: f.restartBehaviour ?? 'basedOnReturnCode',
      },
      minimumSupportedWindowsRelease: '1607',
      returnCodes: f.returnCodes ?? '(defaults)',
    }),
    '',
    '# displayVersion and allowAvailableUninstall are NOT v1.0 properties — they are applied by a',
    '# follow-up PATCH against beta, or Graph accepts the create and drops them.',
  ]
}

/** Content upload — the only step that moves the package, and the only slow one. */
export function graphContentLines(fileName: string): string[] {
  return [
    'POST …/mobileApps/{appId}/microsoft.graph.win32LobApp/contentVersions',
    'POST …/contentVersions/{n}/files',
    '',
    json({
      '@odata.type': '#microsoft.graph.mobileAppContentFile',
      name: fileName,
      size: 0,
      sizeEncrypted: 0,
      isDependency: false,
    }),
    '',
    '# Poll the file until azureStorageUri is issued, PUT the blocks, then:',
    'POST …/files/{fileId}/commit',
    'PATCH …/mobileApps/{appId}   → { "committedContentVersion": "{n}" }',
    '',
    '# Only this step re-uploads content. Behaviour and metadata changes never do.',
  ]
}

/** Graph target, from the mapper's target — `intune_publisher._build_assignment_target` (:2515). */
function graphTarget(t: any, placeholder?: string): Record<string, unknown> {
  const built: Record<string, unknown> = {}
  if (t.type === 'allUsers' || t.type === 'allLicensedUsers') {
    built['@odata.type'] = '#microsoft.graph.allLicensedUsersAssignmentTarget'
  } else if (t.type === 'allDevices') {
    built['@odata.type'] = '#microsoft.graph.allDevicesAssignmentTarget'
  } else {
    built['@odata.type'] = t.include
      ? '#microsoft.graph.groupAssignmentTarget'
      : '#microsoft.graph.exclusionGroupAssignmentTarget'
    built.groupId = placeholder ?? t.id
  }
  if (t.filter_id) {
    built.deviceAndAppManagementAssignmentFilterId = t.filter_id
    built.deviceAndAppManagementAssignmentFilterType = t.filter_type || 'include'
  }
  return built
}

/** Graph settings, from the mapper's settings — `_build_assignment_settings` (:2821). */
function graphSettings(s: any): Record<string, unknown> | undefined {
  if (!s) return undefined
  const out: Record<string, unknown> = {
    '@odata.type': '#microsoft.graph.win32LobAppAssignmentSettings',
  }
  if (s.notifications) out.notifications = s.notifications
  if (s.delivery_optimization) out.deliveryOptimizationPriority = s.delivery_optimization
  if (s.restart_settings) {
    const rs: Record<string, unknown> = {
      '@odata.type': 'microsoft.graph.win32LobAppRestartSettings',
      gracePeriodInMinutes: s.restart_settings.grace_period_minutes ?? 1440,
      countdownDisplayBeforeRestartInMinutes: s.restart_settings.countdown_minutes ?? 15,
    }
    // Snooze has no Graph boolean — it is disabled by OMITTING the duration, so absence stays
    // absence here too.
    if (s.restart_settings.snooze_minutes != null) {
      rs.restartNotificationSnoozeDurationInMinutes = s.restart_settings.snooze_minutes
    }
    out.restartSettings = rs
  }
  if (s.install_time_settings) {
    const its: Record<string, unknown> = {
      '@odata.type': 'microsoft.graph.mobileAppInstallTimeSettings',
      useLocalTime: s.install_time_settings.use_local_time ?? true,
    }
    if (s.install_time_settings.start_datetime) {
      its.startDateTime = s.install_time_settings.start_datetime
    }
    if (s.install_time_settings.deadline_datetime) {
      its.deadlineDateTime = s.install_time_settings.deadline_datetime
    }
    out.installTimeSettings = its
  }
  return out
}

/**
 * ONE `/assign` call carrying the complete assignment set — spec §7a.2, and what
 * `intune_publisher.assign_all` now issues.
 *
 * `/assign` REPLACES the app's assignments; POSTing to the `assignments` collection appends one.
 * The template states the app's whole intended audience, so replace is the matching semantic —
 * with append, a row deleted from the template can never be removed from Intune and a re-publish
 * duplicates every row.
 *
 * One filtered row sends the WHOLE call to beta: in the v1.0 CSDL the target type declares no
 * filter properties, so v1.0 accepts them and discards them, silently unfiltering an assignment
 * whose filter existed to narrow a broad target. The call cannot be split to keep the rest on
 * v1.0, because the second `/assign` would replace what the first one set.
 */
export function graphAssignLines(
  rows: AssignmentRow[],
  _groups: AssignmentGroup[],
  opts?: { generatedGroupName?: GeneratedGroupName; appRef?: string },
): string[] {
  if (!rows.length) return ['# No assignments — the app object is created and targets nobody.']
  const appRef = opts?.appRef ?? '{appId}'
  const assignments: Record<string, unknown>[] = []
  const notes: string[] = []

  for (const row of rows) {
    // A generated group has no id until publish creates it, so the mapper would drop the row.
    // The placeholder names what the create call above will produce.
    const generated = row.groupSource === 'generated' && row.target === 'group'
    const placeholder = generated
      ? `<created: ${opts?.generatedGroupName?.(row) ?? 'generated group'}>`
      : undefined
    const mapped = rowToAssignmentCreate(
      generated ? ({ ...row, groupId: 'pending' } as AssignmentRow) : row,
    )
    if (!mapped) {
      notes.push(`# ${row.intent}: no group chosen — this row is reported, not sent.`)
      continue
    }
    const body: Record<string, unknown> = {
      '@odata.type': '#microsoft.graph.mobileAppAssignment',
      intent: mapped.intent,
      target: graphTarget(mapped.target, placeholder),
    }
    const settings = graphSettings(mapped.settings)
    if (settings) body.settings = settings
    assignments.push(body)
  }

  const filtered = rows.some((r) => r.filter)
  const base = filtered ? 'https://graph.microsoft.com/beta' : 'https://graph.microsoft.com/v1.0'
  return [
    ...notes,
    `POST ${base}/deviceAppManagement/mobileApps/${appRef}/assign`,
    'Content-Type: application/json',
    '',
    json({ mobileAppAssignments: assignments }),
    '',
    '# 200/204. Scope: DeviceManagementApps.ReadWrite.All',
    '# This REPLACES the app’s assignments — it is the whole set, not an addition to it.',
    ...(filtered
      ? ['# beta is required: one row carries a filter, and v1.0 discards filter properties.']
      : []),
  ]
}
